import type { Omit } from '@material-ui/core';

import type { ContentItem, Menu } from '@swf/cms';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import type {
  ExtractDispatchFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import { select } from '@swf/core/store';

export interface CMSNavigationState {
  cacheControl?: string;
  menu?: Menu;
  content?: Record<string, ContentItem>;
  depth?: number;
}

const reducers = {
  preloaded: (
    state: CMSNavigationState,
    payload: Partial<CMSNavigationState>
  ): CMSNavigationState => ({
    ...state,
    depth: payload.depth ?? undefined,
    menu: payload.menu ?? undefined,
    content: payload.content ?? undefined,
    cacheControl: payload.cacheControl
  })
};

const effects = {
  async preload(
    payload: undefined,
    { dispatch, rootState, widget: { id }, request }: WidgetEffectParams<void>
  ) {
    const widgetDispatch: ExtractDispatchFromModel<CMSNavigationWidgetModel> = dispatch[id];
    const path = select.location.stripLocale(rootState, undefined);
    const pathname = select.page.getUrl(rootState)?.pathname || path;

    const { body: data, headers } = await request<Omit<CMSNavigationState, 'cacheControl'>>({
      service: 'cms.navigation',
      body: { path: pathname }
    });

    widgetDispatch.preloaded({ ...data, cacheControl: headers.cacheControl });
  }
};

const selectors = (slice: Slicer<CMSNavigationState>) => ({
  get() {
    return slice;
  }
});

export interface CMSNavigationWidgetModel extends WidgetModelConfig<void, CMSNavigationState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const cmsNavigationWidgetModel: CMSNavigationWidgetModel = {
  state: {},
  reducers,
  effects,
  selectors
};

export const useCmsNavigationWidget: UseWidget<void, CMSNavigationWidgetModel> = useWidget;
